import React from 'react';
import { useFeedback } from '../../context/FeedbackContext';

/**
 * Persistent "Share feedback" pill, fixed bottom-right of the viewport.
 * Desktop-only — on mobile the bottom-right corner is occupied by the
 * `MobileBottomSheet` peek bar and Leaflet attribution, and the auto-prompt
 * is the user's only feedback affordance there.
 *
 * Click opens the Google Form in a new tab so the dashboard tab keeps the
 * user's exploration state. (We tried embedding the form in a modal
 * iframe, but Google Forms refuses to render in an iframe whenever the
 * form has any sign-in or domain restriction — too brittle for a demo.)
 *
 * Hidden while the auto-prompt toast is on screen — the toast is the
 * louder version of this same affordance, showing both would clutter the
 * corner. z-1500 sits above Leaflet panes (cap 700) and the basemap-switch
 * hint (1100), below all modals (≥ 2000) so any open dialog cleanly
 * covers the pill.
 */
export const FeedbackTrigger: React.FC = () => {
  const { openSurvey, promptVisible } = useFeedback();

  if (promptVisible) return null;

  return (
    <button
      type="button"
      onClick={openSurvey}
      aria-label="Share feedback about this dashboard"
      className="hidden md:inline-flex fixed bottom-6 right-6 z-[1500] items-center gap-2 px-4 h-10 rounded-figma-button bg-figma-card border border-figma-mint/60 text-figma-text font-roboto font-bold text-[12px] uppercase tracking-[0.10em] shadow-figma-card hover:bg-figma-panel hover:border-figma-mint transition-smooth"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        className="text-figma-mint"
      >
        <path
          d="M2.5 3.5h11a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H6.5l-3 2.5v-2.5h-1a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
      <span>Share feedback</span>
    </button>
  );
};
